import { ComponentPublicInstance, defineComponent, PropType, ref } from 'vue'

export type Question = {
  q: string
  a: {
    label: string
    value: string
  }[]
}

const QuestionItem = defineComponent({
  name: 'QuestionItem',
  props: {
    idx: { type: Number, required: true },
    q: { type: String, required: true },
    a: { type: Array as PropType<Question['a']>, required: true },
    v: { type: String, required: true },
  },
  emits: {
    input: (_v: string) => true,
  },
  setup (props, { emit }) {
    return () => (
      <div class="py-8 border-b select-none">
        <div class="text-center text-primary text-xl font-bold">
          Q{props.idx + 1}
        </div>
        <div class="text-center mt-4 font-medium text-lg">{props.q}</div>
        <div class="flex mt-4">
          {props.a.map(({ label, value }, idx) => (
            <div class="basis-0 grow" onClick={() => emit('input', value)}>
              <div
                class={[
                  'rounded-full border w-10 h-10 mx-auto flex items-center justify-center font-bold cursor-pointer hover:bg-primary hover:text-white',
                  props.v === value
                    ? 'bg-primary text-white'
                    : 'bg-white text-black',
                ]}
              >
                {idx + 1}
              </div>
              <div
                class={[
                  'text-center mt-2',
                  props.a.length > 5 ? 'text-sm' : '',
                ]}
                style={{ 'word-break': 'keep-all' }}
              >
                {label}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  },
})

const Questions = defineComponent({
  props: {
    questions: { type: Array as PropType<Question[]>, required: true },
    values: { type: Array as PropType<string[]>, required: true },
  },
  emits: { input: (_values: string[]) => true },
  setup (props, { emit }) {
    const refs = ref([] as ComponentPublicInstance[])

    const setValue = (idx: number, v: string) => {
      const values = props.values.slice()
      values[idx] = v
      emit('input', values)

      for (let i = 0; i < props.questions.length; i++) {
        if (values[i] === undefined && refs.value[i]) {
          ;(refs.value[i].$el as HTMLDivElement).scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
          break
        }
      }
    }

    return () => (
      <div class="w-full">
        {props.questions.map(({ q, a }, idx) => (
          <QuestionItem
            ref={(r: ComponentPublicInstance) => {
              refs.value[idx] = r
            }}
            idx={idx}
            a={a}
            q={q}
            v={props.values[idx] || ''}
            onInput={v => setValue(idx, v)}
          />
        ))}
      </div>
    )
  },
})

export default Questions
